import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChartLine,
  FaCoins,
  FaWallet,
  FaExchangeAlt,
  FaClock,
  FaSpinner,
  FaStop,
  FaRedo,
  FaChevronDown,
  FaHistory,
  FaNetworkWired,
  FaArrowUp,
  FaChartArea,
  FaTrash,
  FaTimes
} from 'react-icons/fa';
import ConfirmationModal from './ui/ConfirmationModal';
import { cancelPosition, retryPosition, deletePosition, closePosition } from '../services/api';
import { getTokenSymbolWithFallback } from '../utils/tokenLookup';
import { SafeCryptoIcon } from './CoinSelector';


// Enhanced Container with gradient background
const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background.main};
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const InnerContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

// Enhanced Header with better styling
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 24px 32px;
  background: ${props => props.theme.colors.background.card};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 8px 32px ${props => props.theme.colors.shadow};
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${props => props.theme.colors.text.primary};
  font-size: 2rem;
  font-weight: 700;
  margin: 0;

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
  }
`;

const PositionCount = styled.span`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 16px;
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00d4aa, #00b894);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 8px 32px rgba(0, 212, 170, 0.3);

  .pulse {
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

// Header actions container
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Refresh button
const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 1rem;
  }

  .fa-spin {
    animation: fa-spin 1s infinite linear;
  }

  @keyframes fa-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Enhanced Position Card
const PositionCard = styled(motion.div)`
  background: ${props => props.theme.colors.background.card};
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 24px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 32px ${props => props.theme.colors.shadow};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px ${props => props.theme.colors.shadow};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const CardHeader = styled.div`
  padding: 32px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 32px;
  align-items: center;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

// Token Section with enhanced styling
const TokenSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const TokenInfo = styled.div`
  h3 {
    color: ${props => props.theme.colors.text.primary};
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  p {
    color: ${props => props.theme.colors.text.secondary};
    font-size: 0.95rem;
    margin: 0;
    font-weight: 500;
  }
`;

// Enhanced Stats Grid
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.background.light};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.background.lighter};
    transform: translateY(-2px);
  }
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  width: 140px;

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const StatValue = styled.div`
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
`;

// Interactive Progress Bar
// Progress bar
const ProgressBarWrapper = styled.div`
  width: 100%;
  margin: 24px 0 8px 0;
  position: relative;
  height: 36px;
`;

const ProgressBarBase = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme.colors.background.light};
  border-radius: 4px;
    position: absolute;
  top: 14px;
    left: 0;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 14px;
  left: 0;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  transition: width 0.5s ${props => props.theme.transitions.medium};
  z-index: 1;
`;

const ProgressMarkers = styled.div`
  position: absolute;
  width: 100%;
  top: -10px;
  left: 0;
  height: 36px;
  pointer-events: none;
`;

const Marker = styled.div`
  position: absolute;
  top: 0;
  left: ${props => props.left}%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;

  .marker-label {
    font-size: 0.8rem;
    color: ${props => props.theme.colors.text.primary};
    font-weight: 700;
    margin-bottom: 2px;
    margin-top: 0;
  }
  .marker-dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${props => props.theme.colors.text.primary};
    background: ${props =>
      props.type === 'cp' ? props.theme.colors.accent :
      props.type === 'zp' ? props.theme.colors.status.info :
      props.type === 'tp' ? props.theme.colors.status.success :
      props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.background.card};
  }
`;

// Enhanced Actions Section
const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
`;

const PnLCard = styled.div`
  background: ${props => {
    const value = props.value || 0;
    if (value > 0) return 'linear-gradient(135deg, #00d4aa, #00b894)';
    if (value < 0) return 'linear-gradient(135deg, #ff6b6b, #ee5a52)';
    return 'linear-gradient(135deg, #6c757d, #5a6268)';
  }};
  color: white;
  padding: 20px;
  border-radius: 16px;
  text-align: center;
  min-width: 140px;
  box-shadow: 0 8px 32px ${props => {
    const value = props.value || 0;
    if (value > 0) return 'rgba(0, 212, 170, 0.3)';
    if (value < 0) return 'rgba(255, 107, 107, 0.3)';
    return 'rgba(108, 117, 125, 0.3)';
  }};
`;

const PnLValue = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  margin-bottom: 4px;
`;

const PnLPercentage = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.9;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  box-shadow: 0 0 8px ${props => props.color}40;
`;

const StatusText = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
`;

const ActionButton = styled.button`
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: none;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 1rem;
  }
`;



const ExpandButton = styled(ActionButton)`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);

  &:hover {
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
  }

  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;



// Enhanced Details Section
const DetailsSection = styled(motion.div)`
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0;
  overflow: hidden;
`;

// Loading and Empty States
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: ${props => props.theme.colors.text.secondary};
  
  .empty-icon {
    font-size: 4rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 24px;
  }
  
  h3 {
  color: ${props => props.theme.colors.text.primary};
    font-size: 1.5rem;
    margin-bottom: 16px;
  }
  
  p {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const PositionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PositionsList = ({ positions, loading, walletAddress, onPositionUpdate }) => {
  const [actionLoading, setActionLoading] = useState({});
  const [expandedPositions, setExpandedPositions] = useState(new Set());
  const [livePositions, setLivePositions] = useState(positions);
  const [resolvedSymbols, setResolvedSymbols] = useState(new Map());

  // Resolve missing token symbols
  useEffect(() => {
    const resolveMissingSymbols = async () => {
      const newResolvedSymbols = new Map(resolvedSymbols);
      let hasChanges = false;

      for (const position of positions) {
        if (!position.coin_symbol && position.coin_address && position.dex_name) {
          const cacheKey = `${position.dex_name}:${position.coin_address}`;
          
          if (!newResolvedSymbols.has(cacheKey)) {
            try {
              const symbol = await getTokenSymbolWithFallback(
                position.dex_name, 
                position.coin_address
              );
              
              if (symbol) {
                newResolvedSymbols.set(cacheKey, symbol);
                hasChanges = true;
              }
            } catch (error) {
              console.warn(`Failed to resolve symbol for ${position.coin_address}:`, error);
            }
          }
        }
      }

      if (hasChanges) {
        setResolvedSymbols(newResolvedSymbols);
      }
    };

    if (positions.length > 0) {
      resolveMissingSymbols();
    }
  }, [positions, resolvedSymbols]);

  // Get resolved token symbol
  const getResolvedSymbol = (position) => {
    if (position.coin_symbol) {
      return position.coin_symbol;
    }
    
    if (position.coin_address && position.dex_name) {
      const cacheKey = `${position.dex_name}:${position.coin_address}`;
      return resolvedSymbols.get(cacheKey) || '';
    }
    
    return '';
  };

  // Enhanced button logic with better status handling
  const getActionButtons = (position) => {
    const status = position.status?.toLowerCase();
    const buttons = [];
    
    // Primary action button based on status
    if (['pending_funding', 'failed'].includes(status)) {
      buttons.push({
        variant: 'delete',
        text: 'DELETE',
        action: 'delete',
        icon: <FaTrash />,
        description: 'Remove this position permanently'
      });
    } else if (status === 'open') {
      buttons.push({
        variant: 'close',
        text: 'CLOSE',
        action: 'close',
        icon: <FaStop />,
        description: 'Close position at current market price'
      });
    } else if (['pending', 'ready_to_execute', 'pending_execution'].includes(status)) {
      buttons.push({
        variant: 'cancel',
        text: 'CANCEL',
        action: 'cancel',
        icon: <FaTimes />,
        description: 'Cancel pending position'
      });
    }
    
    // Secondary action buttons
    if (['failed', 'pending_funding'].includes(status)) {
      buttons.push({
        variant: 'retry',
        text: 'RETRY',
        action: 'retry',
        icon: <FaRedo />,
        description: 'Retry failed position'
      });
    }
    
    return buttons;
  };

  // Enhanced status display with better descriptions
  const getStatusInfo = (status) => {
    const statusMap = {
      'open': {
        emoji: '🟢',
        label: 'Active',
        description: 'Position is open and being monitored',
        color: '#10b981'
      },
      'pending': {
        emoji: '🟡',
        label: 'Pending',
        description: 'Position is waiting to be executed',
        color: '#f59e0b'
      },
      'pending_funding': {
        emoji: '💰',
        label: 'Funding Required',
        description: 'Waiting for wallet funding confirmation',
        color: '#3b82f6'
      },
      'confirmed': {
        emoji: '✓',
        label: 'Confirmed',
        description: 'Funding confirmed, ready for execution',
        color: '#22c55e'
      },
      'ready_to_execute': {
        emoji: '⚡',
        label: 'Ready',
        description: 'Position is ready for execution',
        color: '#8b5cf6'
      },
      'pending_execution': {
        emoji: '⏳',
        label: 'Executing',
        description: 'Position is being executed on blockchain',
        color: '#f97316'
      },
      'closed': {
        emoji: '✅',
        label: 'Closed',
        description: 'Position has been closed',
        color: '#059669'
      },
      'failed': {
        emoji: '❌',
        label: 'Failed',
        description: 'Position execution failed',
        color: '#dc2626'
      },
      'cancelled': {
        emoji: '🚫',
        label: 'Cancelled',
        description: 'Position was cancelled by user',
        color: '#6b7280'
      }
    };
    
    return statusMap[status?.toLowerCase()] || {
      emoji: '⚪',
      label: 'Unknown',
      description: 'Unknown position status',
      color: '#6b7280'
    };
  };

  // Enhanced action handler with better error handling and user feedback
  const handlePositionAction = async (action, position) => {
    if (!position?.id) {
      console.error('No position ID provided for action:', action);
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [position.id]: true }));
    
    try {
      let result;
      let successMessage;
      
      switch (action) {
        case 'delete':
          result = await deletePosition(position.id);
          successMessage = `Position for ${getResolvedSymbol(position)} has been deleted`;
          break;
        case 'close':
          result = await closePosition(position.id);
          successMessage = `Position for ${getResolvedSymbol(position)} has been closed`;
          break;
        case 'cancel':
          result = await cancelPosition(position.id);
          successMessage = `Position for ${getResolvedSymbol(position)} has been cancelled`;
          break;
        case 'retry':
          result = await retryPosition(position.id);
          successMessage = `Position for ${getResolvedSymbol(position)} is being retried`;
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      // Show success feedback
      if (result && !result.error) {
        // You could add a toast notification here
        console.log(successMessage);
        
        // Refresh positions
        if (onPositionUpdate && walletAddress) {
          await onPositionUpdate();
        }
      } else {
        throw new Error(result?.error || `Failed to ${action} position`);
      }
      
    } catch (error) {
      console.error(`Error ${action}ing position:`, error);
      // You could add error toast notification here
      alert(`Failed to ${action} position: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [position.id]: false }));
    }
  };

  // Enhanced confirmation modal with better messaging
  const [confirmationModal, setConfirmationModal] = useState({ show: false, position: null, action: null });
  
  const openConfirmationModal = (position, action) => {
    setConfirmationModal({ show: true, position, action });
  };
  
  const closeConfirmationModal = () => {
    setConfirmationModal({ show: false, position: null, action: null });
  };
  
  const handleConfirmAction = async () => {
    if (confirmationModal.position && confirmationModal.action) {
      await handlePositionAction(confirmationModal.action, confirmationModal.position);
      closeConfirmationModal();
    }
  };

  // Get confirmation modal content
  const getConfirmationContent = () => {
    if (!confirmationModal.position || !confirmationModal.action) return {};
    
    const { position, action } = confirmationModal;
    const actionConfigs = {
      delete: {
        title: 'Delete Position',
        message: `Are you sure you want to delete the position for ${getResolvedSymbol(position)}? This action cannot be undone and will permanently remove the position from your portfolio.`,
        confirmText: 'Delete Position',
        type: 'danger'
      },
      close: {
        title: 'Close Position',
        message: `Are you sure you want to close the position for ${getResolvedSymbol(position)}? This will execute a market order to close your position at the current price.`,
        confirmText: 'Close Position',
        type: 'warning'
      },
      cancel: {
        title: 'Cancel Position',
        message: `Are you sure you want to cancel the pending position for ${getResolvedSymbol(position)}? This will stop the position from being executed.`,
        confirmText: 'Cancel Position',
        type: 'warning'
      },
      retry: {
        title: 'Retry Position',
        message: `Are you sure you want to retry the failed position for ${getResolvedSymbol(position)}? This will attempt to execute the position again.`,
        confirmText: 'Retry Position',
        type: 'info'
      }
    };
    
    return actionConfigs[action] || {};
  };

  // Use real position data from SSE stream instead of mock prices
  useEffect(() => {
    if (positions && positions.length > 0) {
      // Use the positions data directly from SSE stream
      // The backend already updates current_price in real-time
      setLivePositions(positions);
      console.log('Updated live positions from SSE data:', positions.length, 'positions');
    }
  }, [positions]);

  // Calculate PnL and PnL% using real position data from SSE
  const calculatePnL = (position) => {
    const entry = position.entry_price || 0;
    const current = position.current_price || entry;
    const amount = position.amount || 0;
    
    if (entry === 0) return { pnl: 0, pnl_percentage: 0 };
    
    // Use the real current_price from SSE stream (updated by backend)
    let pnl = 0;
    let pnl_percentage = 0;
    
    if (position.side === 'long') {
      pnl = (current - entry) * (amount / entry);
      pnl_percentage = ((current - entry) / entry) * 100;
    } else {
      pnl = (entry - current) * (amount / entry);
      pnl_percentage = ((entry - current) / entry) * 100;
    }
    
    return { pnl, pnl_percentage };
  };

  const togglePositionExpansion = (positionId) => {
    const newExpanded = new Set(expandedPositions);
    if (newExpanded.has(positionId)) {
      newExpanded.delete(positionId);
    } else {
      newExpanded.add(positionId);
    }
    setExpandedPositions(newExpanded);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0.00%';
    return `${Number(value).toFixed(2)}%`;
  };



  // Calculate progress bar data
  const getTakeProfitValue = (position) => {
    const entryPrice = position.entry_price || 0;
    if (position.take_profit && position.take_profit > 0) {
      return position.take_profit;
    }
    if (position.take_profit_percentage && entryPrice > 0) {
      if (position.side === 'long') {
        return entryPrice * (1 + position.take_profit_percentage);
      } else {
        return entryPrice * (1 - position.take_profit_percentage);
      }
    }
    if (entryPrice > 0) {
      return position.side === 'long' ? entryPrice * 1.012 : entryPrice * 0.988;
    }
    return entryPrice;
  };

  const calculateProgressData = (position) => {
    const entryPrice = position.entry_price || 0;
    const currentPrice = position.current_price || entryPrice;
    const takeProfit = getTakeProfitValue(position);

    if (entryPrice === 0 || takeProfit === entryPrice) return { progress: 0, cp: 0, zp: 0, tp: 0 };

    const minPrice = Math.min(entryPrice, currentPrice, takeProfit);
    const maxPrice = Math.max(entryPrice, currentPrice, takeProfit);
    const priceRange = maxPrice - minPrice;

    if (priceRange === 0) return { progress: 0, cp: 50, zp: 50, tp: 50 };

    const cp = ((currentPrice - minPrice) / priceRange) * 100;
    const zp = ((entryPrice - minPrice) / priceRange) * 100;
    const tp = ((takeProfit - minPrice) / priceRange) * 100;

    let progress = 0;
    if (position.side === 'long') {
      progress = Math.min(((currentPrice - entryPrice) / (takeProfit - entryPrice)) * 100, 100);
    } else {
      progress = Math.min(((entryPrice - currentPrice) / (entryPrice - takeProfit)) * 100, 100);
    }

    return { progress: Math.max(0, progress), cp, zp, tp };
  };

  // Main component render logic
  if (loading) {
    return (
      <Container>
        <InnerContainer>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading Your Positions... 📊</LoadingText>
          </LoadingContainer>
        </InnerContainer>
      </Container>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <Container>
        <InnerContainer>
          <Header>
        <Title>
          <FaChartLine />
              Trading Positions
              <PositionCount>0</PositionCount>
        </Title>
            <LiveIndicator>
              <div className="pulse"></div>
              <FaCoins />
              Live
            </LiveIndicator>
          </Header>
          
          <EmptyState>
            <FaCoins className="empty-icon" />
            <h3>No Active Positions 📈</h3>
            <p>
              Your trading portfolio is empty. Start by creating your first position 
              to begin tracking your investments and profits! 🚀
            </p>
            <p style={{ marginTop: '16px', fontSize: '0.9rem', opacity: 0.7 }}>
              💡 <strong>Tip:</strong> Click the "Refresh" button above to load your latest positions from the blockchain.
            </p>
          </EmptyState>
        </InnerContainer>
      </Container>
    );
  }

  return (
    <Container>
      <InnerContainer>
        <Header>
      <Title>
        <FaChartLine />
            Trading Positions
            <PositionCount>{positions.length}</PositionCount>
      </Title>
                      <HeaderActions>
              <RefreshButton onClick={onPositionUpdate} disabled={loading}>
                {loading ? <FaSpinner className="fa-spin" /> : <FaRedo />}
                {loading ? 'Loading...' : 'Refresh'}
              </RefreshButton>
              <LiveIndicator>
                <div className="pulse"></div>
                <FaCoins />
                Live
              </LiveIndicator>
            </HeaderActions>
        </Header>

        <PositionsContainer>
          <AnimatePresence>
            {livePositions.map((position) => {
              const isExpanded = expandedPositions.has(position.id);
              const progressData = calculateProgressData(position);
              const { pnl, pnl_percentage } = calculatePnL(position);
              
              return (
                <PositionCard
                  key={position.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  layout
                >
                  <CardHeader>
                    <TokenSection>
                      <SafeCryptoIcon 
                        symbol={getResolvedSymbol(position)} 
                        name={getResolvedSymbol(position)}
                      />
                      <TokenInfo>
                        <h3>
                          {getResolvedSymbol(position)} 
                          {getStatusInfo(position.status).emoji}
                        </h3>
                        <p>
                          Position #{position.id.slice(0, 8)}... • {position.side.toUpperCase()} • {getStatusInfo(position.status).label}
                        </p>
                      </TokenInfo>
                    </TokenSection>

                    <div>
                    <StatsGrid>
                      <StatCard>
                        <StatLabel>
                          <FaWallet />
                          Amount
                        </StatLabel>
                        <StatValue>{formatCurrency(position.amount)}</StatValue>
                      </StatCard>
                      
                      <StatCard>
                        <StatLabel>
                          <FaExchangeAlt />
                          Entry Price
                        </StatLabel>
                        <StatValue>{formatCurrency(position.entry_price)}</StatValue>
                      </StatCard>
                      
                      <StatCard>
                        <StatLabel>
                          <FaClock />
                          Current Price
                        </StatLabel>
                        <StatValue>{formatCurrency(position.current_price || position.entry_price || 0)}</StatValue>
                      </StatCard>
                    </StatsGrid>

                      <ProgressBarWrapper>
                        <ProgressBarBase />
                        <ProgressFill style={{ width: `${progressData.progress}%` }} />
                        <ProgressMarkers>
                          <Marker left={progressData.cp} type="cp"><span className="marker-label">CP</span><span className="marker-dot" /></Marker>
                          <Marker left={progressData.zp} type="zp"><span className="marker-label">ZP</span><span className="marker-dot" /></Marker>
                          <Marker left={progressData.tp} type="tp"><span className="marker-label">TP</span><span className="marker-dot" /></Marker>
                        </ProgressMarkers>
                      </ProgressBarWrapper>
                    </div>

                    <ActionsSection>
                      <PnLCard value={pnl}>
                        <PnLValue>
                          {formatCurrency(pnl)}
                        </PnLValue>
                        <PnLPercentage>
                          ({formatPercentage(pnl_percentage)})
                        </PnLPercentage>
                      </PnLCard>

                      {/* Enhanced Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: 'column' }}>
                        {getActionButtons(position).map((button, index) => (
                          <ActionButton
                            key={index}
                            variant={button.variant}
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmationModal(position, button.action);
                            }}
                            disabled={actionLoading[position.id]}
                            title={button.description}
                          >
                            {actionLoading[position.id] ? <FaSpinner /> : button.icon}
                            {button.text}
                          </ActionButton>
                        ))}
                      </div>

                      <StatusIndicator>
                        <StatusDot color={getStatusInfo(position.status).color} />
                        <StatusText>{getStatusInfo(position.status).label}</StatusText>
                      </StatusIndicator>
                      
                      <ExpandButton 
                        expanded={isExpanded} 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePositionExpansion(position.id);
                        }}
                      >
                        {isExpanded ? 'Hide Details' : 'Full Details'}
                        <FaChevronDown />
                      </ExpandButton>
                    </ActionsSection>
                  </CardHeader>

                  <AnimatePresence>
                    {isExpanded && (
                      <DetailsSection
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Header Section with Token Info and PnL */}
                        <div style={{ 
                          background: `linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary})`,
                          padding: '32px',
                          color: 'white',
                          borderRadius: '0'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                              <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '16px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '700',
                                fontSize: '1.4rem',
                                backdropFilter: 'blur(10px)',
                                border: '2px solid rgba(255, 255, 255, 0.3)'
                              }}>
                                {getResolvedSymbol(position)?.slice(0, 3) || 'TKN'}
                              </div>
                              <div>
                                <h2 style={{ margin: '0 0 8px 0', fontSize: '2rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  {getResolvedSymbol(position) || 'Unknown Token'}
                                  <StatusIndicator>
                                    <StatusDot color={getStatusInfo(position.status).color} />
                                    <StatusText>{getStatusInfo(position.status).label}</StatusText>
                                  </StatusIndicator>
                                </h2>
                                <p style={{ margin: '0 0 4px 0', opacity: '0.9', fontSize: '1rem' }}>
                                  Position #{position.id?.slice(0, 8)}... • {position.side?.toUpperCase()}
                                </p>
                                <p style={{ margin: '0', opacity: '0.9', fontSize: '1rem' }}>
                                  {formatCurrency(position.amount)} • Entry: {formatCurrency(position.entry_price)}
                                </p>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{
                                fontSize: '1.8rem',
                                fontWeight: '700',
                                marginBottom: '4px',
                                color: pnl > 0 ? '#4ade80' : pnl < 0 ? '#f87171' : 'white'
                              }}>
                                {formatCurrency(pnl)}
                              </div>
                              <div style={{
                                fontSize: '1rem',
                                opacity: '0.9',
                                color: pnl_percentage > 0 ? '#4ade80' : pnl_percentage < 0 ? '#f87171' : 'white'
                              }}>
                                {formatPercentage(pnl_percentage)}
                              </div>
                            </div>
                          </div>

                          {/* Profit Journey Progress Bar */}
                          <div style={{ marginBottom: '24px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div style={{ fontSize: '0.9rem', opacity: '0.9', fontWeight: '600' }}>
                                Profit Journey
                              </div>
                              <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', opacity: '0.8' }}>
                                <span>Current: {formatCurrency(position.current_price || position.entry_price)}</span>
                                <span>Target: {formatCurrency(getTakeProfitValue(position))}</span>
                              </div>
                            </div>
                            <div style={{ 
                              width: '100%', 
                              height: '8px', 
                              background: 'rgba(255, 255, 255, 0.2)', 
                              borderRadius: '4px',
                              position: 'relative',
                              marginBottom: '16px'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(((position.current_price || position.entry_price) - position.entry_price) / (getTakeProfitValue(position) - position.entry_price) * 100, 100)}%`,
                                background: 'linear-gradient(90deg, #4ade80, #22c55e)',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: '0.8' }}>
                              <div>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>ZP</div>
                                <div>{formatCurrency(position.entry_price)}</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>CP</div>
                                <div>{formatCurrency(position.current_price || position.entry_price)}</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>TA</div>
                                <div>{formatCurrency(getTakeProfitValue(position))}</div>
                              </div>
                            </div>
                          </div>

                          {/* Network & Exchange Info Row */}
                          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaExchangeAlt />
                              <div>
                                <div style={{ fontSize: '0.8rem', opacity: '0.8' }}>Exchange</div>
                                <div style={{ fontWeight: '600' }}>{position.dex_name || 'pancakeswap'}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaNetworkWired />
                              <div>
                                <div style={{ fontSize: '0.8rem', opacity: '0.8' }}>Network</div>
                                <div style={{ fontWeight: '600' }}>{position.network_id === '1' ? 'Ethereum' : position.network_id === '56' ? 'BSC' : 'BSC'}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaClock />
                              <div>
                                <div style={{ fontSize: '0.8rem', opacity: '0.8' }}>Created</div>
                                <div style={{ fontWeight: '600' }}>{position.created_at ? new Date(position.created_at).toLocaleDateString() : 'Unknown'}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Main Content Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', padding: '32px' }}>
                          {/* Left Column */}
                          <div>
                            {/* Transaction Details */}
                            <div style={{ marginBottom: '32px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                <FaWallet style={{ color: props => props.theme.colors.primary }} />
                                <h3 style={{ margin: 0, color: props => props.theme.colors.text.primary }}>Transaction Details</h3>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaCoins style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Amount</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary }}>
                                    {position.amount} {position.coin_symbol}
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaArrowUp style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Entry Price</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary }}>
                                    {formatCurrency(position.entry_price)}
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaClock style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Created</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary }}>
                                    {position.created_at ? new Date(position.created_at).toLocaleDateString() : 'Unknown'}
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaExchangeAlt style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Transactions</span>
                                  </div>
                                  <div style={{ fontSize: '0.8rem' }}>
                                    {position.transactions && position.transactions.length > 0 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {position.transactions.map((tx, index) => (
                                          <div key={index} style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            padding: '4px 8px',
                                            background: props => props.theme.colors.background.dark,
                                            borderRadius: '6px',
                                            fontSize: '0.75rem'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                              <span style={{ 
                                                color: props => props.theme.colors.status.info,
                                                fontWeight: '600',
                                                textTransform: 'capitalize'
                                              }}>
                                                {tx.tx_type}
                                              </span>
                                              <span style={{ 
                                                color: tx.status === 'confirmed' ? props => props.theme.colors.status.success : 
                                                       tx.status === 'failed' ? props => props.theme.colors.status.error : 
                                                       props => props.theme.colors.status.warning,
                                                fontSize: '0.7rem'
                                              }}>
                                                {tx.status}
                                              </span>
                                            </div>
                                            <a 
                                              href={`https://bscscan.com/tx/${tx.tx_hash}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{ 
                                                color: props => props.theme.colors.status.info, 
                                                textDecoration: 'underline',
                                                fontSize: '0.7rem'
                                              }}
                                            >
                                              {tx.tx_hash.slice(0, 8)}...{tx.tx_hash.slice(-6)}
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    ) : position.tx_hash ? (
                                      // Fallback to legacy single transaction display
                                      <a 
                                        href={`https://bscscan.com/tx/${position.tx_hash}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: props => props.theme.colors.status.info, textDecoration: 'underline' }}
                                      >
                                        {position.tx_hash.slice(0, 10)}...{position.tx_hash.slice(-8)}
                                      </a>
                                    ) : (
                                      <span style={{ color: props => props.theme.colors.text.secondary }}>No transactions</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Position History */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                <FaHistory style={{ color: props => props.theme.colors.primary }} />
                                <h3 style={{ margin: 0, color: props => props.theme.colors.text.primary }}>Position History</h3>
                              </div>
                              <div style={{ 
                                background: props => props.theme.colors.background.light, 
                                padding: '20px', 
                                borderRadius: '12px',
                                border: `1px solid ${props => props.theme.colors.border}`
                              }}>
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary }}>Position Created</div>
                                    <div style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>
                                      {new Date(position.created_at).toLocaleString()}
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '0.9rem', color: props => props.theme.colors.text.secondary }}>
                                    Position created for {position.amount} {position.coin_symbol} at {formatCurrency(position.entry_price)}
                                  </div>
                                </div>
                                
                                {position.status === 'OPEN' && (
                                  <div style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                      <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary }}>Trade Executed</div>
                                      <div style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>
                                        {new Date(position.updated_at || position.created_at).toLocaleString()}
                                      </div>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: props => props.theme.colors.text.secondary }}>
                                      Successfully purchased {position.amount} {position.coin_symbol} tokens
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div>
                            {/* Analytics Section */}
                            <div style={{ marginBottom: '32px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                <FaChartLine style={{ color: props => props.theme.colors.primary }} />
                                <h3 style={{ margin: 0, color: props => props.theme.colors.text.primary }}>Analytics</h3>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaArrowUp style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Current Price</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary, marginBottom: '4px' }}>
                                    {formatCurrency(position.current_price || position.entry_price)}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>
                                    Live Price
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaCoins style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Unrealized P&L</span>
                                  </div>
                                  <div style={{ 
                                    fontWeight: '600', 
                                    color: pnl > 0 ? '#4ade80' : pnl < 0 ? '#f87171' : props => props.theme.colors.text.primary,
                                    marginBottom: '4px'
                                  }}>
                                    {formatCurrency(pnl)}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: pnl_percentage > 0 ? '#4ade80' : pnl_percentage < 0 ? '#f87171' : props => props.theme.colors.text.secondary
                                  }}>
                                    {formatPercentage(pnl_percentage)}
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaChartArea style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Take Profit</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary, marginBottom: '4px' }}>
                                    {formatCurrency(position.take_profit || 0)}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>
                                    {position.entry_price && position.take_profit ? 
                                      `${(((position.take_profit - position.entry_price) / position.entry_price) * 100).toFixed(1)}% target` : 
                                      'Not set'
                                    }
                                  </div>
                                </div>
                                <div style={{ 
                                  background: props => props.theme.colors.background.light, 
                                  padding: '16px', 
                                  borderRadius: '12px',
                                  border: `1px solid ${props => props.theme.colors.border}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <FaWallet style={{ color: props => props.theme.colors.primary }} />
                                    <span style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>Total Fees</span>
                                  </div>
                                  <div style={{ fontWeight: '600', color: props => props.theme.colors.text.primary, marginBottom: '4px' }}>
                                    {formatCurrency(position.fee || 0)}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: props => props.theme.colors.text.secondary }}>
                                    Gas + Trading
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Performance Chart */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                <FaChartArea style={{ color: props => props.theme.colors.primary }} />
                                <h3 style={{ margin: 0, color: props => props.theme.colors.text.primary }}>Performance Chart</h3>
                              </div>
                              <div style={{ 
                                background: props => props.theme.colors.background.light, 
                                padding: '20px', 
                                borderRadius: '12px',
                                border: `1px solid ${props => props.theme.colors.border}`,
                                height: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: props => props.theme.colors.text.secondary,
                                fontSize: '0.9rem'
                              }}>
                                Chart visualization coming soon...
                              </div>
                            </div>
                          </div>
                        </div>
                        </DetailsSection>
                    )}
                  </AnimatePresence>
                </PositionCard>
              );
            })}
          </AnimatePresence>
        </PositionsContainer>

        <ConfirmationModal
          isOpen={confirmationModal.show}
          onClose={closeConfirmationModal}
          onConfirm={handleConfirmAction}
          title={confirmationModal.action ? getConfirmationContent().title : ''}
          message={confirmationModal.action ? getConfirmationContent().message : ''}
          confirmText={confirmationModal.action ? getConfirmationContent().confirmText : ''}
          type={confirmationModal.action ? getConfirmationContent().type : ''}
        />
      </InnerContainer>
    </Container>
  );
};

export default PositionsList; 
